/**
 * SignIn.tsx -- Authentication entry screen (FR-016, v.1-DD SS11).
 *
 * Ported from the legacy src/screens/auth/SignIn.tsx with Cohere 2026 visual
 * language. Three rendering states driven by `useConvexAuth()` + allowlist:
 *   1. Loading          -- auth state resolving OR OAuth redirect in flight
 *   2. Sign In          -- unauthenticated user: Google OAuth button only.
 *                          No anonymous-fallback button (PRD SS3 "Out of scope").
 *   3. Access Denied    -- authenticated user not in allowedUsers table
 *
 * Design tokens (Cohere 2026):
 *   - Canvas: var(--color-surface-shiro, #FAF2EA)
 *   - Card: kinari via Card depth="md"
 *   - Accent: sora via Button primary
 *
 * Accessibility:
 *   - Loading state announces via role="status" + aria-live="polite"
 *   - Buttons use primitive Button's focus indicator (2px sora outline)
 *   - Access Denied heading marked with role="alert"
 */
import '../../styles/screens/auth.css';
import React, { useCallback, useState } from "react";
import type { CSSProperties } from "react";
import { useQuery } from "convex/react";
import { useAuth } from "@workos-inc/authkit-react";
import { api } from "../../../../../convex/_generated/api";
import { Icon } from "../../components/primitives";
import { useT } from "../../i18n/useT";

// ---------------------------------------------------------------------------
// Shared Layout
// ---------------------------------------------------------------------------

function ScreenFrame({ children }: { children: React.ReactNode }) {
  const t = useT();

  const wrapperStyle: CSSProperties = {
    minHeight: "100vh",
    backgroundColor: "var(--color-surface-shiro, #FAF2EA)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: "var(--font-display)",
    color: "var(--color-surface-sumi, #1A1614)",
  };

  const cardWrapperStyle: CSSProperties = {
    width: "100%",
    maxWidth: "420px",
    backgroundColor: "var(--color-surface-kinari, #F0E6D6)",
    borderRadius: "22px",
    padding: "32px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  };

  return (
    <div style={wrapperStyle}>
      <div style={cardWrapperStyle}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading State
// ---------------------------------------------------------------------------

function LoadingState({ label = "Loading..." }: { label?: string }) {
  const containerStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    padding: "16px 0",
  };

  const spinnerStyle: CSSProperties = {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    border: "3px solid var(--color-surface-murasaki, #E8DDD0)",
    borderTopColor: "var(--color-surface-sora, #5CB8DB)",
    animation: "signin-spin 0.9s linear infinite",
  };

  const textStyle: CSSProperties = {
    fontSize: "14px",
    color: "var(--color-surface-sumi, #1A1614)",
    opacity: 0.7,
  };

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={containerStyle}
    >
      <style>
        {"@keyframes signin-spin { to { transform: rotate(360deg); } }"}
      </style>
      <div style={spinnerStyle} aria-hidden="true" />
      <span style={textStyle}>{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Google OAuth Button Icon
// ---------------------------------------------------------------------------

function GoogleGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path
        fill="var(--color-ink-inverse, #FFFFFF)"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="var(--color-ink-inverse, #FFFFFF)"
        opacity="0.95"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="var(--color-ink-inverse, #FFFFFF)"
        opacity="0.85"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="var(--color-ink-inverse, #FFFFFF)"
        opacity="0.9"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Unauthenticated: Google OAuth only (no anonymous-fallback -- PRD SS3)
// ---------------------------------------------------------------------------

function UnauthenticatedState() {
  const { signIn } = useAuth();
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignIn = useCallback(async () => {
    setError(null);
    setRedirecting(true);
    try {
      // Redirects to the WorkOS AuthKit hosted UI (provider selection there).
      await signIn();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : String(err ?? "Sign in failed");
      setError(message);
      setRedirecting(false);
    }
  }, [signIn]);

  const headingStyle: CSSProperties = {
    fontFamily: "var(--font-display)",
    fontSize: "20px",
    fontWeight: 600,
    letterSpacing: "-0.01em",
    margin: 0,
    color: "var(--color-surface-sumi, #1A1614)",
  };

  const subtitleStyle: CSSProperties = {
    fontSize: "14px",
    margin: 0,
    color: "var(--color-surface-sumi, #1A1614)",
    opacity: 0.7,
  };

  const errorStyle: CSSProperties = {
    fontSize: "13px",
    color: "var(--color-status-failed, #C4453C)",
    margin: 0,
  };

  const contentStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "16px",
    padding: "8px 0",
  };

  const buttonStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px 24px",
    backgroundColor: "var(--color-surface-sora, #5CB8DB)",
    color: "#FFFFFF",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "opacity 0.15s ease",
    fontFamily: "inherit",
  };

  if (redirecting) {
    return <LoadingState label="Signing in..." />;
  }

  return (
    <div style={contentStyle}>
      <div
        style={{ display: "flex", flexDirection: "column", gap: "4px" }}
      >
        <h1 style={headingStyle}>Welcome to Capicua</h1>
        <p style={subtitleStyle}>Sign in to continue.</p>
      </div>
      <button
        type="button"
        style={buttonStyle}
        onClick={() => void handleSignIn()}
        aria-label="Sign in"
      >
        <GoogleGlyph />
        <span>Sign in</span>
      </button>
      {error ? (
        <p role="alert" style={errorStyle}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Access Denied
// ---------------------------------------------------------------------------

function AccessDeniedState({ email }: { email?: string | null }) {
  const { signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
  }, [signOut]);

  const contentStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "12px",
    padding: "8px 0",
  };

  const iconWrapStyle: CSSProperties = {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: "rgba(196,69,60,0.1)",
    color: "var(--color-status-failed, #C4453C)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
    fontWeight: 600,
  };

  const headingStyle: CSSProperties = {
    fontFamily: "var(--font-display)",
    fontSize: "18px",
    fontWeight: 600,
    margin: 0,
    color: "var(--color-surface-sumi, #1A1614)",
  };

  const bodyStyle: CSSProperties = {
    fontSize: "14px",
    color: "var(--color-surface-sumi, #1A1614)",
    opacity: 0.75,
    margin: 0,
    maxWidth: "320px",
  };

  const signOutButtonStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 20px",
    backgroundColor: "transparent",
    color: "var(--color-surface-sumi, #1A1614)",
    border: "1px solid var(--color-surface-murasaki, #E8DDD0)",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
  };

  return (
    <div style={contentStyle}>
      <div style={iconWrapStyle} aria-hidden="true">
        <Icon name="x" size={24} />
      </div>
      <h2 role="alert" style={headingStyle}>
        Access Denied
      </h2>
      <p style={bodyStyle}>
        {email
          ? `${email} is not authorized to access this dashboard.`
          : "Your account is not authorized to access this dashboard."}{" "}
        Contact an administrator if you believe this is a mistake.
      </p>
      <button
        type="button"
        style={signOutButtonStyle}
        onClick={() => void handleSignOut()}
        disabled={signingOut}
      >
        {signingOut ? "Signing out..." : "Sign out"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public Entry
// ---------------------------------------------------------------------------

/**
 * SignIn -- Authentication entry screen.
 *
 * Renders one of three states based on Convex Auth and allowlist status:
 *   - Loading indicator while auth or allowlist resolve
 *   - Google OAuth sign-in button for unauthenticated visitors
 *   - Access Denied card for authenticated users not in `allowedUsers`
 *
 * No anonymous sign-in button (PRD SS3: "Out of scope: anonymous sign-in path").
 */
export function SignIn() {
  const { isLoading, user } = useAuth();
  const isAuthenticated = user !== null;

  const currentUser = useQuery(
    api.users.currentUser,
    isAuthenticated ? {} : "skip",
  );

  // 1. Initial auth resolution
  if (isLoading) {
    return (
      <ScreenFrame>
        <LoadingState label="Loading..." />
      </ScreenFrame>
    );
  }

  // 2. Authenticated: gate on allowlist
  if (isAuthenticated) {
    if (currentUser === undefined) {
      return (
        <ScreenFrame>
          <LoadingState label="Checking access..." />
        </ScreenFrame>
      );
    }

    const isDenied =
      currentUser === null ||
      (typeof currentUser === "object" &&
        currentUser !== null &&
        "isAllowed" in currentUser &&
        (currentUser as { isAllowed?: boolean }).isAllowed === false);

    if (isDenied) {
      const email =
        currentUser && typeof currentUser === "object" && "email" in currentUser
          ? ((currentUser as { email?: string | null }).email ?? null)
          : null;
      return (
        <ScreenFrame>
          <AccessDeniedState email={email} />
        </ScreenFrame>
      );
    }

    // Authenticated AND allowed -- parent shell routes away
    return (
      <ScreenFrame>
        <LoadingState label="Welcome back..." />
      </ScreenFrame>
    );
  }

  // 3. Unauthenticated -- Google OAuth only
  return (
    <ScreenFrame>
      <UnauthenticatedState />
    </ScreenFrame>
  );
}

export default SignIn;
